import { SunIcon, MoonIcon, MonitorIcon } from "./Icons.jsx";
import BrandLockup from "./BrandLockup.jsx";
import { nextTheme, THEME_LABEL } from "./theme.js";
import LanguageSwitcher from "./LanguageSwitcher.jsx";
import { useT } from "./i18n/I18nContext.jsx";

// Shared chrome for every public (logged-out) page — landing's own header,
// a title block, and the footer nav linking help/privacy/terms/refunds/contact.
export function PublicFooter({ onNavigate }) {
  const t = useT();
  const links = [
    ["pricing", "Pricing"],
    ["help", "Help"],
    ["privacy", "Privacy"],
    ["terms", "Terms"],
    ["refunds", "Refunds"],
    ["contact", "Contact"],
  ];
  // Pre-rendered static pages (scripts/build-pseo.mjs) — real <a> links, not
  // onNavigate, since these are separate .html files, not SPA-rendered views.
  const guides = [
    ["/freelancers", "For freelancers"],
    ["/real-estate-agents", "For real estate agents"],
    ["/rideshare-drivers", "For rideshare drivers"],
    ["/mileage-tracking", "Mileage tracking"],
    ["/quarterly-expense-reports", "Quarterly reports"],
    ["/bookkeeper-access", "Bookkeeper access"],
  ];
  return (
    <>
      <div className="public-footer-links">
        {links.map(([id, label]) => (
          <a
            key={id}
            className="link-btn"
            href={`/${id}`}
            onClick={(e) => {
              // Plain left-click: handle in-app (no full reload). Anything
              // else (middle-click, ctrl/cmd-click, shift-click) falls
              // through to the real href so "open in new tab" etc. work.
              if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                onNavigate(id);
              }
            }}
          >
            {label}
          </a>
        ))}
      </div>
      <div className="public-footer-links public-footer-guides">
        {guides.map(([href, label]) => (
          <a key={href} className="link-btn" href={href}>
            {label}
          </a>
        ))}
      </div>
      <div className="public-footer-links public-footer-guides public-footer-family">
        <span>{t("footer.familyLabel")}</span>
        <a className="link-btn" href="https://anyainow.ai">{t("footer.anyainow")}</a>
        <a className="link-btn" href="https://imageainow.com">{t("footer.imageainow")}</a>
      </div>
    </>
  );
}

export function ThemeToggle({ theme, setTheme }) {
  if (!theme || !setTheme) return null;
  return (
    <button
      className="theme-toggle"
      onClick={() => setTheme(nextTheme)}
      title={`Theme: ${THEME_LABEL[theme]} — click to change`}
    >
      {theme === "system" ? (
        <MonitorIcon className="icon" />
      ) : theme === "dark" ? (
        <MoonIcon className="icon" />
      ) : (
        <SunIcon className="icon" />
      )}
    </button>
  );
}

export default function PublicPage({
  title,
  lastUpdated,
  onNavigate,
  onGetStarted,
  theme,
  setTheme,
  children,
}) {
  const t = useT();
  return (
    <div className="app landing-app">
      <header className="landing-header">
        <button className="link-btn" onClick={() => onNavigate("landing")}>
          <BrandLockup />
        </button>
        <div className="header-actions">
          <LanguageSwitcher />
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <button className="secondary" onClick={onGetStarted}>
            {t("common.signIn")}
          </button>
        </div>
      </header>
      <main className="landing-main legal-page">
        <h1 className="landing-h1 legal-h1">{title}</h1>
        {lastUpdated && <p className="muted legal-updated">Last updated: {lastUpdated}</p>}
        {children}
        <PublicFooter onNavigate={onNavigate} />
      </main>
    </div>
  );
}
